import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { customSort } from '@components/student-list/utils';
import { CHECKED } from '@components/checkbox/constants';
import { ACCURACY, PROGRESS } from '@components/accuracy-progress-toggle/accuracy-progress-constants';
import { ASSIGNED, HIGHEST } from '@components/assignment-filters/filters-constants';
import { CURRENTLY_ASSIGNED } from '@components/quick-selector/quick-selector-constants';
import {
  findVoluntaryAndAssignedLearningJourneyResult,
  getLearningJourneyResultByAccuracyFilter,
  getLearningJourneyResultByProgressFilter
} from '@components/status-cell/helpers';

import { assignmentFilterType, MAX_LEARNING_JOURNEY_COUNT, SORT_DIRECTION_TYPES } from '@helpers/constants';
import PortalCommunication from '@helpers/portal-comms';
import LocalStorage from '../../helpers/local-storage';

import {
  getTopicsAndStandards,
  updateStrandAndStandards,
  getFilteredStrandAndStandards,
  updateStrands,
  removeStandard,
  filterStandardByLJCount,
  getSelectedStandardCount,
  sortStudentAssignmentsById,
  getAllLearningJourneys,
  updateAssignmentsHidden,
  checkAccuracyCellSelectable,
  checkProgressCellSelectable
} from './upf-assign-util';
import * as actionTypes from './upf-assign-action-types';

export const initialState = {
  accuracyProgressToggle: {
    accuracySubFilter: HIGHEST,
    currentFilter: ACCURACY,
    progressSubFilter: HIGHEST
  },
  actionInProgress: false,
  assignmentStatuses: [],
  classrooms: [],
  displayModalKey: -1,
  filteredStudents: [],
  genericCourses: {},
  isLoadingAssignmentStatuses: false,
  isLoadingGenericCourses: false,
  isRestricted: true,
  pausePolling: false,
  publicationCourses: [],
  publications: [],
  selectedAssignmentFilterType: assignmentFilterType.ASSIGN_UPF,
  selectedAssignmentHashKeys: {},
  selectedCells: [],
  selectedClass: { value: '', label: '' },
  selectedGroup: { value: '', label: '' },
  selectedLearningJourneyIds: [],
  selectedPublication: { value: '', label: '' },
  selectedCourseOption: { value: '', label: '' },
  selectedStandardCount: 0,
  selectedStudentIds: [],
  selectedTopics: [],
  selectedCourse: {
    id: null,
    version: null
  },
  showGridLoader: true,
  showSearchPanel: false,
  sortDirection: SORT_DIRECTION_TYPES.ASC,
  sortValue: 'lastName',
  sortedStudents: [],
  sortedStudentIds: [],
  standardCount: 0,
  strands: [],
  studentAssignments: [],
  studentUnAssignments: [],
  studentsAssignmentCount: [],
  studentsAssignmentStatus: [],
  topics: [],
  unsavedChanges: false
};

export default (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.FILTERED_STUDENTS: {
      const { sortDirection, sortValue, studentsAssignmentStatus } = state;
      const filteredStudents = customSort(action.payload, sortValue, sortDirection);

      return {
        ...state,
        filteredStudents,
        selectedStudentIds: [],
        studentAssignments: [],
        studentUnAssignments: [],
        sortedStudents: filteredStudents,
        studentsAssignmentStatus: sortStudentAssignmentsById(studentsAssignmentStatus, filteredStudents),
        pausePolling: false
      };
    }

    case actionTypes.STUDENTS_ASSIGNMENTS_STATUSES_REQUEST: {
      return {
        ...state,
        isLoadingAssignmentStatuses: true
      };
    }

    case actionTypes.STUDENTS_ASSIGNMENTS_STATUSES_SUCCESS: {
      const { unsavedChanges } = state;

      if (unsavedChanges) {
        return {
          ...state,
          showGridLoader: false,
          unsavedChanges: true
        };
      }
      return {
        ...state,
        assignmentStatuses: action.payload.assignmentStatuses,
        studentsAssignmentStatus: updateAssignmentsHidden(
          sortStudentAssignmentsById(action.payload.assignmentStatuses, state.sortedStudents),
          state.selectedLearningJourneyIds
        ),
        isLoadingAssignmentStatuses: false,
        pausePolling: false,
        psrQuestionSummaries: action.payload.psrQuestionSummaries || [],
        selectedPSRs: [],
        selectedStudentIds: [],
        showGridLoader: false,
        studentAssignments: [],
        studentUnAssignments: [],
        studentsAssignmentCount: action.payload.studentAssignmentCounts,
        unsavedChanges: false
      };
    }

    case actionTypes.UPDATE_SORTED_STUDENTS_LIST: {
      const { sortDirection, sortValue, studentsAssignmentStatus } = state;

      const newStudentOrder = customSort(action.students, sortValue, sortDirection);

      return {
        ...state,
        sortedStudents: newStudentOrder,
        studentsAssignmentStatus: sortStudentAssignmentsById(studentsAssignmentStatus, newStudentOrder)
      };
    }

    case actionTypes.UPDATE_SORT_PROPERTIES: {
      return {
        ...state,
        sortDirection: action.direction,
        sortValue: action.sortValue
      };
    }

    case actionTypes.ERROR_DETECTED: {
      const message = `${'Oops an error has occurred. '}
      ${(<a href="http://www.3plearning.com/contact/">Please contact Tech Support</a>)}
      ${' with details of what happened and we will work to get you back on track.'}`;
      PortalCommunication.serverError(message);

      return {
        ...state,
        isLoadingAssignmentStatuses: false,
        isLoadingGenericCourses: false
      };
    }

    case actionTypes.PUBLICATIONS_ACQUIRED: {
      return {
        ...state,
        publications: action.payload || []
      };
    }

    case actionTypes.GENERIC_COURSES_REQUEST: {
      return {
        ...state,
        isLoadingGenericCourses: true
      };
    }

    case actionTypes.GENERIC_COURSES_SUCCESS: {
      const genericCourses = action.payload || [];
      let topicsAndStandards = [];
      topicsAndStandards = action.savedFilter || getTopicsAndStandards(genericCourses);
      const selectedTopics = action.savedFilter
        ? topicsAndStandards
        : filterStandardByLJCount(topicsAndStandards, MAX_LEARNING_JOURNEY_COUNT);
      const { selectedStandardCount, standardCount } = getSelectedStandardCount(selectedTopics);

      return {
        ...state,
        genericCourses,
        isLoadingGenericCourses: false,
        selectedLearningJourneyIds: getAllLearningJourneys(selectedTopics),
        selectedStandardCount,
        selectedTopics,
        standardCount,
        strands: topicsAndStandards,
        studentAssignments: [],
        selectedAssignmentHashKeys: {},
        topics: selectedTopics,
        selectedCourse: {
          id: action.courseId,
          version: action.courseVersion
        }
      };
    }

    case actionTypes.CLASSROOMS_ACQUIRED: {
      PortalCommunication.loadingSpinnerOff();
      return {
        ...state,
        classrooms: action.payload || []
      };
    }

    case actionTypes.UPDATE_STRANDS: {
      const { strands } = state;
      const { id, checked, searchTerm, actionType } = action;
      const clonedStrands = cloneDeep(strands);

      let updatedTopicData = [];
      let updatedStrandAndStandards = [];

      switch (actionType) {
        case 'selection': {
          updatedStrandAndStandards = updateStrandAndStandards(id, checked, clonedStrands);
          if (searchTerm && searchTerm !== '') {
            const clonedData = cloneDeep(updatedStrandAndStandards);
            updatedTopicData = getFilteredStrandAndStandards(searchTerm, clonedData);
          } else {
            updatedTopicData = cloneDeep(updatedStrandAndStandards);
          }
          break;
        }

        case 'uncheckAll':
          updatedStrandAndStandards = updateStrands(false, clonedStrands);
          updatedTopicData = updatedStrandAndStandards;
          break;

        case 'reset':
          updatedStrandAndStandards = updateStrands(true, clonedStrands);
          updatedTopicData = updatedStrandAndStandards;
          break;

        default:
          {
            updatedStrandAndStandards = strands;
            const clonedStrandData = cloneDeep(updatedStrandAndStandards);
            updatedTopicData = getFilteredStrandAndStandards(searchTerm, clonedStrandData);
          }
          break;
      }

      const { selectedStandardCount, standardCount } = getSelectedStandardCount(updatedTopicData);
      return {
        ...state,
        strands: updatedStrandAndStandards,
        topics: updatedTopicData,
        selectedStandardCount,
        standardCount
      };
    }

    case actionTypes.APPLY_FILTER: {
      const { selectedCourse, strands, selectedStandardCount, standardCount } = state;
      const { id, version } = selectedCourse;
      const key = `${version}_${id}_FILTER`;
      const savedFilter = LocalStorage.getItem(key);
      if (savedFilter) {
        LocalStorage.removeItem(key);
      }
      LocalStorage.setItem(key, strands);
      return {
        ...state,
        selectedTopics: strands,
        selectedLearningJourneyIds: getAllLearningJourneys(strands),
        selectedStandardCount,
        selectedAssignmentHashKeys: {},
        standardCount
      };
    }

    case actionTypes.UPDATE_SELECTED_COURSE: {
      return {
        ...state,
        selectedCourse: action.selectedCourse,
        studentAssignments: [],
        selectedStudentIds: [],
        studentUnAssignments: [],
        pausePolling: false
      };
    }

    case actionTypes.SHOW_HIDE_SEARCH_PANEL: {
      const { strands } = state;
      strands.forEach(strand => {
        strand.isExpand = !!strand.childTopics.find(s => s.selected);
      });
      return {
        ...state,
        showSearchPanel: action.status,
        strands,
        topics: strands
      };
    }

    case actionTypes.DISPLAY_MODAL_INFORMATION: {
      return {
        ...state,
        displayModalKey: action.key
      };
    }

    case actionTypes.HIDE_STANDARD: {
      const { selectedTopics, studentsAssignmentStatus, strands } = state;
      const updatedSelectedTopics = removeStandard(action.id, selectedTopics);
      const { selectedStandardCount } = getSelectedStandardCount(updatedSelectedTopics);
      const learningJourneys = getAllLearningJourneys(updatedSelectedTopics);

      const topic = strands.map(strand => ({
        ...strand,
        childTopics: strand.childTopics.map(t => {
          if (t.id === action.id) {
            return {
              ...t,
              selected: false,
              isExpand: false,
              childTopics: t.childTopics?.map(c => ({ ...c, selected: false }))
            };
          }
          return t;
        })
      }));

      return {
        ...state,
        topics: updatedSelectedTopics,
        strands: topic,
        selectedTopics: updatedSelectedTopics,
        selectedLearningJourneyIds: getAllLearningJourneys(updatedSelectedTopics),
        studentsAssignmentStatus: updateAssignmentsHidden(studentsAssignmentStatus, learningJourneys),
        selectedStandardCount
      };
    }

    case actionTypes.UPDATE_STUDENT_ASSIGNMENT: {
      const { studentsAssignmentStatus, selectedCells } = state;
      let newSelectedCells = Number(selectedCells) || 0;

      const statusIndex = action.payload.index;
      const assignmentStatus = studentsAssignmentStatus[statusIndex];
      const updatedStudentsAssignmentStatus = [...studentsAssignmentStatus];
      const updatedIsSelected = !assignmentStatus?.metaData?.isSelected;

      updatedStudentsAssignmentStatus[statusIndex] = {
        ...assignmentStatus,
        metaData: {
          ...assignmentStatus?.metaData,
          isSelected: updatedIsSelected
        }
      };

      if (updatedIsSelected) {
        newSelectedCells += 1;
      } else {
        newSelectedCells -= 1;
      }

      return {
        ...state,
        studentsAssignmentStatus: updatedStudentsAssignmentStatus,
        selectedCells: newSelectedCells,
        pausePolling: newSelectedCells > 0
      };
    }

    case actionTypes.ASSIGN_REQUEST: {
      return {
        ...state,
        actionInProgress: true
      };
    }

    case actionTypes.ASSIGN_SUCCESS_REQUEST: {
      return {
        ...state,
        actionInProgress: false,
        studentAssignments: [],
        studentUnAssignments: [],
        selectedStudentIds: []
      };
    }

    case actionTypes.ASSIGN_FAILURE_REQUEST: {
      return {
        ...state,
        actionInProgress: false
      };
    }

    case actionTypes.GRID_CELL_SELECTION: {
      const { indexes = [], status } = action.payload;
      const { studentsAssignmentStatus, selectedCells } = state;
      const updatedStudentsAssignmentStatus = [...studentsAssignmentStatus];
      const isSelected = status === CHECKED;

      const numberSelectedCells = Number(selectedCells) || 0;
      const changeCount = indexes.length;
      const newSelectedCells = isSelected ? numberSelectedCells + changeCount : numberSelectedCells - changeCount;

      // Minor mutation to significantly reduce computation expense
      indexes.forEach(index => {
        const studentAssignmentStatus = studentsAssignmentStatus[index];
        const { learningJourneyResults, stepsCount } = studentAssignmentStatus || {};

        const learningJourneyResult = getLearningJourneyResultByProgressFilter(learningJourneyResults, ASSIGNED);

        if (stepsCount === learningJourneyResult?.stepsCompleted) {
          return;
        }

        if (studentAssignmentStatus) {
          updatedStudentsAssignmentStatus[index].metaData.isSelected = isSelected;
        }
      });

      return {
        ...state,
        studentsAssignmentStatus: updatedStudentsAssignmentStatus,
        selectedCells: newSelectedCells,
        pausePolling: newSelectedCells > 0
      };
    }

    case actionTypes.UNASSIGN_REQUEST: {
      return {
        ...state,
        actionInProgress: true
      };
    }

    case actionTypes.UNASSIGN_SUCCESS_REQUEST: {
      return {
        ...state,
        actionInProgress: false,
        studentUnAssignments: [],
        studentAssignments: []
      };
    }

    case actionTypes.UNASSIGN_FAILURE_REQUEST: {
      return {
        ...state,
        actionInProgress: false
      };
    }

    case actionTypes.EXPAND_COLLAPSE_REQUEST: {
      const { topics } = state;
      const cloneTopics = [...topics];
      const topic = cloneTopics.find(t => t.id === action.strandId);
      topic.isExpand = !topic.isExpand;

      return {
        ...state,
        topics: cloneTopics
      };
    }

    case actionTypes.SET_SELECTED_CLASS: {
      return {
        ...state,
        selectedClass: action.selectedClass,
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.SET_SELECTED_GROUP: {
      return {
        ...state,
        selectedGroup: action.selectedGroup,
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.SET_SELECTED_PUBLICATION: {
      return {
        ...state,
        selectedPublication: action.selectedPublication,
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.SET_SELECTED_COURSE_OPTION: {
      return {
        ...state,
        selectedCourseOption: action.selectedCourseOption,
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.RESTRICT_ACCESS_FAILURE: {
      return { ...state, actionInProgress: false };
    }

    case actionTypes.RESTRICT_ACCESS_REQUEST: {
      return { ...state, actionInProgress: true };
    }

    case actionTypes.RESTRICT_ACCESS_SUCCESS: {
      const { selectedGroup } = state;

      return {
        ...state,
        actionInProgress: false,
        isRestricted: action.payload,
        selectedGroup: {
          ...selectedGroup,
          isRestricted: action.payload
        }
      };
    }

    case actionTypes.SET_ASSIGNMENT_FILTER_TYPE: {
      const studentsAssignmentStatus = state.studentsAssignmentStatus.map(status => ({
        ...status,
        metaData: {
          ...status.metaData,
          isSelected: false
        }
      }));
      return {
        ...state,
        selectedAssignmentFilterType: action.assignmentType,
        studentsAssignmentStatus,
        studentAssignments: [],
        studentUnAssignments: [],
        selectedStudentIds: [],
        selectedCells: 0,
        pausePolling: false
      };
    }

    case actionTypes.SELECT_ACCURACY_PROGRESS_TOGGLE: {
      return {
        ...state,
        accuracyProgressToggle: {
          ...state.accuracyProgressToggle,
          currentFilter: action.selectedFilter
        }
      };
    }

    case actionTypes.SELECT_ACCURACY_SUB_FILTER: {
      return {
        ...state,
        accuracyProgressToggle: {
          ...state.accuracyProgressToggle,
          accuracySubFilter: action.accuracySubFilter
        },
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.SELECT_PROGRESS_SUB_FILTER: {
      return {
        ...state,
        accuracyProgressToggle: {
          ...state.accuracyProgressToggle,
          progressSubFilter: action.progressSubFilter
        },
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.SELECT_ASSIGNMENT_HASH_KEY: {
      const { assignmentHashKey, selectedValues } = action;
      const updatedSelectedAssignmentHashKeys = {
        ...state.selectedAssignmentHashKeys
      };

      if (updatedSelectedAssignmentHashKeys[assignmentHashKey]) {
        delete updatedSelectedAssignmentHashKeys[assignmentHashKey];
      } else {
        updatedSelectedAssignmentHashKeys[assignmentHashKey] = selectedValues;
      }

      return {
        ...state,
        selectedAssignmentHashKeys: updatedSelectedAssignmentHashKeys
      };
    }

    case actionTypes.SELECT_ASSIGNMENT_HASH_KEYS: {
      const { assignmentHashKeys } = action;

      return {
        ...state,
        selectedAssignmentHashKeys: {
          ...state.selectedAssignmentHashKeys,
          ...assignmentHashKeys
        }
      };
    }

    case actionTypes.UNSELECT_ASSIGNMENT_HASH_KEYS: {
      const { assignmentHashKeys } = action;

      const updatedSelectedAssignmentHashKeys = {
        ...state.selectedAssignmentHashKeys
      };

      Object.keys(assignmentHashKeys).forEach(assignmentHashKey => {
        if (updatedSelectedAssignmentHashKeys[assignmentHashKey]) {
          delete updatedSelectedAssignmentHashKeys[assignmentHashKey];
        }
      });

      return {
        ...state,
        selectedAssignmentHashKeys: updatedSelectedAssignmentHashKeys
      };
    }

    case actionTypes.QUICK_SELECTION: {
      const { selectionType } = action.payload;
      const { currentFilter, accuracySubFilter, progressSubFilter } = state.accuracyProgressToggle;

      let updatedSelection;
      let totalCellSelected = 0;
      const isProgressFilterSelected = currentFilter === PROGRESS;

      const getLearningJourneyResult = isProgressFilterSelected
        ? getLearningJourneyResultByProgressFilter
        : getLearningJourneyResultByAccuracyFilter;

      const selectedSubFilter = isProgressFilterSelected ? progressSubFilter : accuracySubFilter;

      const updatedStudents = state.studentsAssignmentStatus.map(studentAssignmentStatus => {
        const { learningJourneyResults, stepsCount } = studentAssignmentStatus;
        /*
          For currently assigned, the subfilter doesn't matter
          So, we need to get the learningJourneyResults without applying filter
        */
        const learningJourneyResult =
          selectionType === CURRENTLY_ASSIGNED
            ? findVoluntaryAndAssignedLearningJourneyResult(learningJourneyResults)
            : getLearningJourneyResult(learningJourneyResults, selectedSubFilter);

        const isSelectable = isProgressFilterSelected
          ? checkProgressCellSelectable(selectionType, learningJourneyResult, stepsCount)
          : checkAccuracyCellSelectable(selectionType, learningJourneyResult);

        totalCellSelected = isSelectable ? totalCellSelected + 1 : totalCellSelected;

        updatedSelection = {
          ...studentAssignmentStatus.metaData,
          isSelected: isSelectable
        };

        return {
          ...studentAssignmentStatus,
          metaData: updatedSelection
        };
      });
      return {
        ...state,
        studentsAssignmentStatus: updatedStudents,
        pausePolling: totalCellSelected > 0
      };
    }

    case actionTypes.RESET_SELECTED_ASSIGNMENT_HASH_KEYS: {
      return {
        ...state,
        selectedAssignmentHashKeys: {}
      };
    }

    case actionTypes.DISABLE_SHOW_GRID_LOADER: {
      return {
        ...state,
        showGridLoader: false
      };
    }

    case actionTypes.UPDATE_SORTED_STUDENT_IDS: {
      return {
        ...state,
        sortedStudentIds: [...action.sortedStudentIds]
      };
    }

    default:
      return {
        ...state
      };
  }
};
